import { useState, useEffect, useRef, useMemo } from "react";
import MaleIcon from "../../../../assets/icons/maid-module-icons/male.svg";
import FemaleIcon from "../../../../assets/icons/maid-module-icons/female.svg";
import OthersIcon from "../../../../assets/icons/maid-module-icons/others.svg";
import BikeIcon from "../../../../assets/icons/maid-module-icons/bike.svg";
import CarIcon from "../../../../assets/icons/maid-module-icons/car.svg";
import CrossIcon from "../../../../assets/icons/maid-module-icons/CrossIcon.svg";
import BlueArrow from "../../../../assets/icons/maid-module-icons/BlueArrow.svg";
import ImagePreview from "../../utils/ImagePreview";
import { commonAPI } from "../../../../services/CommonAPIService";
import ImagePreviewModal from "../../modals/modals/ImagePreviewModal";


const genders = [
  { label: "Male", icon: MaleIcon },
  { label: "Female", icon: FemaleIcon },
  { label: "Others", icon: OthersIcon },
];

const vehicles = [
  { label: "4WHEELER", icon: CarIcon },
  { label: "2WHEELER", icon: BikeIcon },
];

const UpdateDetails = ({ show, onHide, staffDetails, onSuccess }) => {

  /* UserDetails State */
  const [profileImage, setProfileImage] = useState(null);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // Camera states & logic
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const myUserData = useMemo(() => {
    const auth = localStorage.getItem("userData");
    if (!auth) return null;
    return JSON.parse(auth);
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // Prefill Data
  useEffect(() => {
    if (staffDetails && show) {
      setName(staffDetails?.userName || "");
      const formattedGender =
  staffDetails?.gender
    ? staffDetails.gender.charAt(0).toUpperCase() +
      staffDetails.gender.slice(1).toLowerCase()
    : "";
    
setGender(formattedGender);
     setVehicleType(staffDetails?.vehicleType || "");
      setVehicleNumber(staffDetails?.vehicleNumber || "");

      if (staffDetails?.userImageURL) {
      setProfileImage(staffDetails.userImageURL);
      }
    }
  }, [staffDetails, show]);

  // Cleanup camera stream on unmount 
  useEffect(() => {
    return () => stopCamera();
  }, []);

  // Clear vehicle number when vehicle type is unselected
  useEffect(() => {
    if (!vehicleType) {
      setVehicleNumber("");
    }
  }, [vehicleType]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    stopCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
        await videoRef.current.play().catch((err) => {
          console.error("Video play error:", err);
          setCameraError("Could not start camera preview.");
        });
      }

      setShowCamera(true);
    } catch (err) {
      console.error("Camera access error:", err);
      let msg = "Cannot access camera.";
      if (err.name === "NotAllowedError") msg = "Camera permission denied.";
      if ( err.name === "NotReadableError") msg = "Camera is in use by another application.";
      if (err.name === "NotFoundError") msg = "No camera detected on this device.";
      setCameraError(msg);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.82);

    fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `profile-${Date.now()}.jpg`, { type: "image/jpeg" });
        setProfileImage(file);
        setShowCamera(false);
        stopCamera();
      })
      .catch((err) => console.error("Photo capture failed:", err));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB");
      return;
    }

    setProfileImage(file);
  };

  // Submit handler for update
const handleSubmit = async () => {
  if (!myUserData) {
    alert("User data not found. Please log in again.");
    return;
  }

  if (!name.trim()) {
    alert("Full name is required");
    return;
  }

  if (!gender) {
    alert("Gender is required");
    return;
  }

  if (!staffDetails || !staffDetails.staffId) {
    alert("Staff details not available for update.");
    return;
  }

  setLoading(true);

  let payload = null; 

  try {
    const valueToSend = {};
    const trimmedName = name.trim();
    const trimmedVehicleNumber = vehicleNumber.trim();

    if (trimmedName !== (staffDetails.userName?.trim() || "")) {
      valueToSend.Name = trimmedName;
    }

    if (gender !== (staffDetails.gender || "")) {
      valueToSend.Gender = gender;
    }

    if (vehicleType !== (staffDetails.vehicleType || "")) {
      valueToSend.VehicleType = vehicleType;
    }

    if (
      trimmedVehicleNumber !==
      (staffDetails.vehicleNumber?.trim() || "")
    ) {
      valueToSend.VehicleNumber = trimmedVehicleNumber;
    }

    if (
      profileImage instanceof File ||
      (typeof profileImage === "string" &&
        profileImage !== (staffDetails.userImageURL || ""))
    ) {
      valueToSend.UserImageURL = profileImage;
    }

    if (Object.keys(valueToSend).length === 0) {
      alert("No changes detected.");
      onHide();
      return;
    }

    payload = {
      staffId: staffDetails.staffId,
      key: "PERSONALINFO",
      value: valueToSend,
      modifiedUserId: String(staffDetails.staffId),
    };

    const response = await commonAPI("UpdateServiceStaffDetail", payload, {
      buildingId: myUserData.buildingId,
    });

    if (response?.success === true) {
      onSuccess();
      onHide();
    } else {
      alert(response?.message || "Failed to save details");
    }

  } catch (error) {
    console.error("Error saving staff details:", error);

    const bodyError = {
      userId: myUserData?.userId,
      mobileNumber: myUserData?.mobileNumber,
      requestJSON: payload ? JSON.stringify(payload) : null,
      uri: "/api/User/ServiceStaffV1",
      errorSource: "CuroNextPortal",
      errorNumber: "UPDTS01",
      errorSummary: error?.message,
      errorDescription: error?.message,
      fileClassName: "UpdateDetails.js",
      functionName: "handleSubmit",
    };

    try {
      await commonAPI("ErrorLogMaster", bodyError);
    } catch (logErr) {
      console.error("Error while logging error:", logErr);
    }

    alert("An error occurred. Please try again.");
  } finally {
    setLoading(false);
  }
};
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  if (!show) return null;

  const isSubmitDisabled = loading || !name.trim() || !gender || (vehicleType && !vehicleNumber.trim());

  const openPreview = () => {
    if (profileImage) {
      let url;
      if (profileImage instanceof File) {
        url = URL.createObjectURL(profileImage);
      } else {
        url = profileImage;
      }
      setPreviewUrl(url);
      setPreviewTitle("Profile Photo");
      setShowModal(true);
    }
  };

  const handleClosePreview = () => {
    setShowModal(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setPreviewTitle("");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1080,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1090,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 16px",
        }}
      >
        <div
          className="bg-white d-flex flex-column"
          style={{
            width: "100%",
            maxWidth: 600,
            height: "96vh",
            maxHeight: "calc(100vh - 32px)",
            borderRadius: 18,
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center text-white"
            style={{
              backgroundColor: "#1C80D2",
              padding: "12px 16px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
           
              <h5
                className="mb-0"
                style={{
                  fontSize: "clamp(14px, 4vw, 18px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Edit Staff Details
              </h5>
            </div>
            <button
              className="btn p-0 d-flex align-items-center justify-content-center bg-white"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                flexShrink: 0,
              }}
              onClick={onHide}
            >
              <img src={CrossIcon} alt="cross" />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px",
            }}
          >
            {/* Profile Photo + Name */}
            <div className="d-flex align-items-start gap-4 mb-4">
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div onClick={profileImage ? openPreview : null} style={profileImage ? {cursor: "pointer"} : {}}>
                  <ImagePreview image={profileImage} setImage={setProfileImage} />
                </div>

                <input
                  type="file"
                  accept="image/*"
                  id="profile-upload"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                {!profileImage && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0,0,0,0.45)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => document.getElementById("profile-upload")?.click()}
                  >
                    <div className="text-center text-white small">
                      <div>Upload</div>
                      <div>or</div>
                      <button
                        type="button"
                        className="btn btn-sm btn-light mt-2 px-3 py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          startCamera();
                        }}
                      >
                        Camera
                      </button>
                    </div>
                  </div>
                )}
                {profileImage && (
                  <button
                    className="btn btn-sm btn-outline-secondary position-absolute start-50 translate-middle-x"
                    style={{
                      top: "100%",         
                       
                      fontSize: "0.75rem",
                      padding: "4px 10px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setProfileImage(null)}
                  >
                    Retake
                  </button>
                )}

              </div>

              {/* Name */}
              <div className="flex-grow-1">
                <label
                  className="form-label fw-bold mb-2"
                  style={{
                    fontSize: "clamp(12px, 2.5vw, 14px)",
                    color: "#374151",
                  }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    height: 48,
                    fontSize: "clamp(14px, 3vw, 16px)",
                    borderRadius: 8,
                  }}
                />
              </div>
            </div>

            {/* Gender */}
            <div className="mb-4">
              <label className="form-label fw-bold mb-3" style={{marginTop:"28px"}}>Gender</label>
              <div className="d-flex flex-wrap gap-3">
                {genders.map((g) => {
                  const isActive = gender === g.label;
                  return (
                    <div
                      key={g.label}
                            onClick={() => {
                if (isActive) {
                  setGender(null); 
                } else {
                  setGender(g.label);
                }
              }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 20px",
                        borderRadius: 12,
                        cursor: "pointer",
                        border: isActive ? "2px solid #1C80D2" : "1.5px solid #ced4da",
                        background: isActive ? "#e7f3ff" : "white",
                        transition: "all 0.18s",
                      }}
                    >
                      <img src={g.icon} alt={g.label} width={26} height={26} />
                      <span
                        style={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1C80D2" : "#444",
                          fontSize: "clamp(13px, 3vw, 15px)",
                        }}
                      >
                        {g.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Vehicle Type */}
            <div className="mb-4">
              <label
                className="form-label fw-bold mb-3"
                style={{
                  fontSize: "clamp(12px, 2.5vw, 14px)",
                  color: "#374151",
                }}
              >
                Vehicle Type
              </label>
              <div className="d-flex gap-3">
                {vehicles.map((v) => {
                  const isActive = vehicleType === v.label;
                  return (
                    <div
                      key={v.label}
                             onClick={() => {
                if (isActive) {
                  setVehicleType(null); 
                } else {
                  setVehicleType(v.label);
                }
              }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 24px",
                        borderRadius: 12,
                        cursor: "pointer",
                        border: isActive ? "2px solid #1C80D2" : "1.5px solid #ced4da",
                        background: isActive ? "#e7f3ff" : "white",
                        transition: "all 0.18s",
                      }}
                    >
                      <img src={v.icon} alt={v.label} width={32} height={24} />
                      <span
                        style={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1C80D2" : "#444",
                          fontSize: "clamp(13px, 3vw, 15px)",
                        }}
                      >
                        {v.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Vehicle Number (optional) */}
            <div className="mb-4">
              <label
                className="form-label fw-bold mb-2"
                style={{
                  fontSize: "clamp(12px, 2.5vw, 14px)",
                  color: "#374151",
                }}
              >
                Vehicle Number {vehicleType ? "" : <small className="text-muted">(optional)</small>}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. DL 7C AA 1234"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                disabled={!vehicleType}
                style={{
                  height: 48,
                  fontSize: "clamp(14px, 3vw, 16px)",
                  textTransform: "uppercase",
                  borderRadius: 8,
                }}
              />
            </div>
          </div>

          {/* Footer with buttons */}
          <div style={{ padding: "16px", borderTop: "1px solid #dee2e6" }}>
            <div className="d-flex gap-3">
              <button
                onClick={handleSubmit}
                className="btn btn-primary flex-fill"
                disabled={isSubmitDisabled}
                style={{
                  borderRadius: 30,
                  padding: "clamp(12px, 3vw, 14px)",
                  fontWeight: 600,
                  fontSize: "clamp(14px, 3vw, 15px)",
                  backgroundColor: "#1C80D2",
                  border: "none",
                }}
              >
                {loading ? "Updating..." : "Update"}
              </button>
              <button
                onClick={onHide}
                className="btn  flex-fill"
                disabled={loading}
                style={{
                  borderRadius: 30,
                  padding: "clamp(12px, 3vw, 14px)",
                  fontWeight: 600,
                  fontSize: "clamp(14px, 3vw, 15px)",
                  borderColor: "#1C80D2",
                  color: "#1C80D2",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── CAMERA OVERLAY ─── */}
      {showCamera && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.88)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              background: "#000",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "cover",
                borderRadius: 16,
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          <div
            style={{
              padding: 24,
              background: "#111",
              display: "flex",
              justifyContent: "center",
              gap: 40,
            }}
          >
            <button
              onClick={capturePhoto}
              className="btn btn-success btn-lg px-5 py-3"
              style={{ borderRadius: 50, fontSize: 18 }}
            >
              Capture
            </button>

            <button
              onClick={() => {
                stopCamera();
                setShowCamera(false);
              }}
              className="btn btn-outline-light px-5 py-3"
              style={{ fontSize: 18 }}
            >
              Cancel
            </button>
          </div>

          {cameraError && (
            <div className="alert alert-warning m-4 text-center">{cameraError}</div>
          )}
        </div>
      )}

      <ImagePreviewModal
        show={showModal}
        imageUrl={previewUrl}
        onClose={handleClosePreview}
        title={previewTitle}
      />
    </>
  );
};

export default UpdateDetails;